import { defineComponent, reactive } from '@vue/composition-api';
import MessageConst from '@/shared/message-const';
import CommonFunction from '@/shared/common-function';

// リアクティブな変数の型定義
export type reactiveData = {
    dropAreaHtml: string;                     // ドロップエリアに表示するHTML文
    dropAreaCss: string;                      // ドロップエリアのCSS
    error: string;                            // ファイル選択時のエラー内容
    fileMaxSize: number;                      // 最大ファイルサイズ
    permitExtensions: string[];               // 選択可能なファイル拡張子
};

/**
 * 引数
 */
interface Props {
    fileMaxSize: number,                      // 最大ファイルサイズ
    permitExtensions: string[],               // 選択可能なファイル拡張子
}

/**
 * ファイルアップロードエリアコンポーネント
 */
export default defineComponent({
    props: {
        fileMaxSize: Number,
        permitExtensions: Array
    },
    setup(props: Props, context) {
        const state: reactiveData = reactive<reactiveData>({
            dropAreaHtml: MessageConst.I_FILE_DROP_AREA_MESSAGE,
            dropAreaCss: 'default-drop-area',
            error: '',
            fileMaxSize: 0,
            permitExtensions: []
        });

        /**
         * フォルダ選択ボタンをクリックし、ファイル選択画面を表示する
         */
        function showFolderSelect(): void {
            const elementUserExcelFile = document.getElementById('inputFile');
            if (elementUserExcelFile !== null) {
                elementUserExcelFile.click();
            }
        }

        /**
         * ドロップ、ファイル選択イベント
         * ファイル情報を表示する
         * ファイル情報を親コンポーネントに通知する
         */
        function onFileSet(event: any): void {
            state.dropAreaHtml = MessageConst.I_FILE_DROP_AREA_MESSAGE;  // ドロップエリアメッセージを初期化
            state.error = '';                                            // エラーをクリア
            state.dropAreaCss = 'default-drop-area';                     // 実線を破線にし、ドロップエリアの背景色をもとに戻す
            // ファイル情報取得
            const file = event.target.files
                ? event.target.files[0]                                  // 選択ファイル
                : event.dataTransfer.files[0];                           // ドロップファイル

            // ファイルサイズチェック(0バイトチェック)
            if (file.size === 0) {
                state.error = CommonFunction.replaceArray(MessageConst.E_FILE_NO_DATA, [state.fileMaxSize / 1024]);
                context.emit('clearFile');                               // 親コンポーネントにファイル情報クリアを通知する
                return;
            }

            // ファイルサイズチェック(最大サイズチェック)
            if (!!state.fileMaxSize && file.size > state.fileMaxSize) {
                state.error = CommonFunction.replaceArray(MessageConst.E_FILE_MAX_SIZE, [state.fileMaxSize / 1024]);
                context.emit('clearFile');                               // 親コンポーネントにファイル情報クリアを通知する
                return;
            }

            // ファイル拡張子チェック
            if (!!state.permitExtensions && state.permitExtensions.indexOf(CommonFunction.getExtension(file.name)) === -1) {
                state.error = CommonFunction.replaceArray(MessageConst.E_FILE_EXTENSION, [
                    '(' + state.permitExtensions + ')'
                ]);
                context.emit('clearFile');                              // 親コンポーネントにファイル情報クリアを通知する
                return;
            }

            // ドロップエリアにファイル情報を表示
            state.dropAreaHtml = CommonFunction.replaceArray(MessageConst.I_FILE_INFO, [
                file.name,
                (file.size/1024).toLocaleString()
            ]);

            // ファイル情報を親コンポーネントに通知する
            context.emit('setFile', file);
        }

        /**
         * ファイルをドラックオーバーした場合にドロップエリアの見た目を変更
         */
        function accentDropArea(): void {
            // 破線を実線にし、ドロップエリアの背景色を灰色にする
            state.dropAreaCss = 'file-over-drop-area';
        }

        /**
         * ファイルをドラックリーブした場合にドロップエリアの見た目を元に戻す
         */
        function defaultDropArea(): void {
            // 実線を破線にし、ドロップエリアの背景色をもとに戻す
            state.dropAreaCss = 'default-drop-area';
        }

        /** 
         * DBから取得したファイルサイズ、選択可能拡張子を取得する
         */
        function setParam(fileMaxSize: number, permitExtension: string[]): void {
            state.fileMaxSize = fileMaxSize;
            state.permitExtensions = permitExtension;
        }

        return {
            state,
            showFolderSelect,
            onFileSet,
            accentDropArea,
            defaultDropArea,
            setParam
        };
    }
});
